/** @typedef {{ id: number, code: string, displayName: string }} CompanySource */

/** @type {CompanySource[]} */
export const HARDCODED_COMPANY_SOURCES = [
    { id: 1, code: 'misfits_market', displayName: 'Misfits Market' },
    { id: 2, code: 'blue_apron', displayName: 'Blue Apron' },
    { id: 3, code: 'marley_spoon', displayName: 'Marley Spoon' },
];

/** @type {number} MM fallback company source id */
export const MM_FALLBACK_COMPANY_SOURCE_ID = 1;

/** Exportable partner badge background colors keyed by company source code. */
export const COMPANY_SOURCE_BADGE_COLORS = {
    misfits_market: '#F1C34A',
    blue_apron: '#7dA0FF',
    marley_spoon: '#B774F2',
};

/** Two-letter abbreviations shown inside partner badges. */
export const COMPANY_SOURCE_BADGE_ABBREVIATIONS = {
    misfits_market: '1P',
    blue_apron: 'BA',
    marley_spoon: 'MS',
};

const companySourceById = new Map(HARDCODED_COMPANY_SOURCES.map((source) => [source.id, source]));
const companySourceByCode = new Map(HARDCODED_COMPANY_SOURCES.map((source) => [source.code, source]));

/**
 * @param {number|string|null|undefined} id
 * @returns {CompanySource|undefined}
 */
export function getCompanySourceById(id) {
    if (id === null || id === undefined || id === '') return undefined;
    const numericId = Number(id);
    if (!Number.isFinite(numericId)) return undefined;
    return companySourceById.get(numericId);
}

/**
 * @param {string|null|undefined} code
 * @returns {CompanySource|undefined}
 */
export function getCompanySourceByCode(code) {
    if (!code) return undefined;
    return companySourceByCode.get(String(code).trim().toLowerCase());
}

/**
 * @param {CompanySource} source
 * @returns {{ value: number, label: string, code: string }}
 */
export function toCompanySourceOption(source) {
    return {
        value: source.id,
        label: source.displayName,
        code: source.code,
    };
}

/**
 * Resolves company source id from row/API shapes.
 *
 * @param {Object|null|undefined} row
 * @returns {number|undefined}
 */
export function resolveCompanySourceId(row) {
    if (!row) return undefined;
    if (row.companySourceId != null && row.companySourceId !== '') {
        const fromId = Number(row.companySourceId);
        if (Number.isFinite(fromId)) return fromId;
    }
    if (row.companySourceID != null && row.companySourceID !== '') {
        const fromId = Number(row.companySourceID);
        if (Number.isFinite(fromId)) return fromId;
    }
    const code = row.companySource ?? row.company_source;
    if (code) {
        return getCompanySourceByCode(code)?.id;
    }
    return undefined;
}

/**
 * Maps a UI row/draft to the API company source id field.
 *
 * @param {Object|null|undefined} row
 * @returns {number|undefined}
 */
export function toApiCompanySourceId(row) {
    const id = resolveCompanySourceId(row);
    if (id == null) return undefined;
    return getCompanySourceById(id)?.id;
}
